/**
 * The map panel: chrome, count, and the map itself (BON-538).
 *
 * The count is of venues PLOTTED, not venues in scope. They differ whenever a
 * venue has no coordinates, and saying "80 venues" over a map showing 74 pins
 * invites someone to go looking for six that were never there.
 */
import { CountBadge } from '../../primitives/CountBadge';

import { useRegionColor } from './region-colors-context';
import { plottable } from './venue-map-layers';
import { VenueMap } from './VenueMap';
import type { VenueRosterRow } from './venues-roster-api';

export interface VenueMapCardProps {
  venues: readonly VenueRosterRow[];
  /** Changes only when the scope changes; see `useVenueMap`. */
  fitKey: string;
  /** The venue the roster is pointing at; its pin swells. */
  hoveredId?: string | null;
  onHoverVenue?: (id: string | null) => void;
  onSelectVenue?: (id: string) => void;
  /**
   * The map's height. A number is a fixed band, as the tablet layout draws it;
   * `'fill'` makes the card take its parent's height, which is what the desktop
   * column wants — the map is as tall as the roster beside it.
   */
  height: number | 'fill';
}

export function VenueMapCard({
  venues,
  fitKey,
  height,
  hoveredId,
  onHoverVenue,
  onSelectVenue,
}: VenueMapCardProps) {
  const shown = plottable(venues);
  const fills = height === 'fill';
  const colorOf = useRegionColor();

  return (
    <section
      className="nb-card nb-card-tight"
      style={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: fills ? '100%' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          padding: '14px 18px',
          borderBottom: '1px solid var(--nb-border)',
        }}
      >
        <h2 className="nb-display nb-eyebrow" style={{ margin: 0, fontSize: 12 }}>
          Venue map
        </h2>
        <CountBadge count={shown.length} noun="venue" />
        {shown.length < venues.length && (
          <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)', marginLeft: 'auto' }}>
            {venues.length - shown.length} without coordinates
          </span>
        )}
      </div>
      <div
        style={{ height: fills ? undefined : height, flex: fills ? 1 : undefined, minHeight: 240 }}
      >
        <VenueMap
          venues={venues}
          colorOf={colorOf}
          fitKey={fitKey}
          hoveredId={hoveredId}
          onHoverVenue={onHoverVenue}
          onSelectVenue={onSelectVenue}
        />
      </div>
    </section>
  );
}
